'use client';

import { useState, useEffect } from 'react';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { MobileDatePicker } from '@mui/x-date-pickers/MobileDatePicker';
import dayjs, { Dayjs } from 'dayjs';
import { createTheme, ThemeProvider } from '@mui/material/styles';

const theme = createTheme({
  palette: {
    primary: {
      main: '#161616',
    },
    background: {
      paper: '#f8f5f3',
    },
  },
  typography: {
    fontFamily: '"HalvarMittelschrift", sans-serif',
  },
  components: {
    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundColor: '#f8f5f3',
          borderRadius: '0.5rem',
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: '1rem',
          '& .MuiButton-root': {
            fontSize: '0.875rem',
            fontWeight: 'bold',
            textTransform: 'uppercase',
            fontFamily: '"HalvarMittelschrift", sans-serif',
            letterSpacing: '0.05em',
            padding: '0.5rem 1.25rem',
            borderRadius: '9999px',
            minWidth: 'auto',
            '&:first-of-type': {
              color: '#7a7a7a',
            },
            '&:last-of-type': {
              backgroundColor: '#0057B8',
              color: '#ffffff',
              '&:hover': {
                backgroundColor: '#004999',
              },
            },
          },
        },
      },
    },
  },
});

type Props = {
  isOpen: boolean;
  onClose: () => void;
  value: string; // YYYY-MM-DD format
  onChange: (value: string) => void;
  minDate?: string;
  label?: string;
};

export function DatePickerModal({
  isOpen,
  onClose,
  value,
  onChange,
  minDate,
  label = "Select date",
}: Props) {
  // Track selected date internally until user confirms
  const [tempValue, setTempValue] = useState<Dayjs | null>(value ? dayjs(value) : null);

  // Reset temp value when modal opens or external value changes
  useEffect(() => {
    if (isOpen) {
      setTempValue(value ? dayjs(value) : null);
    }
  }, [isOpen, value]);

  const handleChange = (newValue: Dayjs | null) => {
    setTempValue(newValue);
  };

  const handleAccept = () => {
    if (tempValue) {
      onChange(tempValue.format('YYYY-MM-DD'));
    }
    onClose();
  };

  const handleCancel = () => {
    setTempValue(value ? dayjs(value) : null);
    onClose();
  };

  return (
    <ThemeProvider theme={theme}>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <MobileDatePicker
          open={isOpen}
          onClose={handleCancel}
          onAccept={handleAccept}
          value={tempValue}
          onChange={handleChange}
          closeOnSelect={false}
          minDate={minDate ? dayjs(minDate) : undefined}
          label={label}
          localeText={{
            okButtonLabel: 'Done',
            cancelButtonLabel: 'Cancel',
          }}
          slotProps={{
            textField: {
              sx: { display: 'none' },
            },
            toolbar: {
              hidden: false,
              sx: {
                backgroundColor: '#0057B8',
                color: '#ffffff',
                '& .MuiTypography-root': {
                  color: '#ffffff',
                  fontFamily: '"HalvarMittelschrift", sans-serif',
                },
                '& .MuiDatePickerToolbar-title': {
                  fontSize: '1.5rem',
                  fontWeight: 'bold',
                },
              },
            },
            calendarHeader: {
              sx: {
                '& .MuiPickersCalendarHeader-label': {
                  fontSize: '1.125rem',
                  fontWeight: 'bold',
                  fontFamily: '"HalvarMittelschrift", sans-serif',
                },
              },
            },
            day: {
              sx: {
                fontSize: '1rem',
                fontFamily: '"HalvarMittelschrift", sans-serif',
                '&.Mui-selected': {
                  backgroundColor: '#161616',
                  '&:hover': {
                    backgroundColor: '#333333',
                  },
                },
                '&.MuiPickersDay-today': {
                  borderColor: '#161616',
                },
              },
            },
          }}
        />
      </LocalizationProvider>
    </ThemeProvider>
  );
}

// Helper to format date for display
export function formatDateForDisplay(dateString: string): string {
  if (!dateString) return '';
  const date = dayjs(dateString);
  return date.format('MMM D, YYYY'); // e.g., "Dec 4, 2025"
}
